import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Rate } from '../../../shared/types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Langkawi escape early and enjoy the added luxury of a complimentary round-trip ferry transfer from Kuala Kedah to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-9464">
      <div className="flex justify-between items-center" data-id="element-9465">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-9466">
          {t('pages.Rates.rates')}
        </h1>
        <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-9467">
          <PlusIcon size={18} className="mr-1" data-id="element-9468" />
          {t('pages.Rates.add_rates')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-9469">
        <input type="search" aria-label={t('pages.Rates.attrs.search_rates')} placeholder={t('pages.Rates.attrs.search_rates')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9470" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-9471">
          <option value="all" data-id="element-9472">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-9473">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-9474">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-9475">
          <option value="all" data-id="element-9476">{t('pages.Rates.room_type')}</option>
          <option value="DELUXE" data-id="element-9477">{t('pages.Rates.deluxe')}</option>
          <option value="SUPERIOR" data-id="element-9478">{t('pages.Rates.superior')}</option>
          <option value="OVERWATER_VILLA" data-id="element-9479">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-9480">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-9481">
          <thead className="bg-gray-50" data-id="element-9482">
            <tr data-id="element-9483">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9484">
                {t('pages.Rates.details')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9485">
                {t('pages.Rates.promo_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9486">
                {t('pages.Rates.base_nightly_rate')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9487">
                {t('pages.Rates.cancellation_plan')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9488">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9489">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-9490">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-9491">
                <td className="px-6 py-4" data-id="element-9492">
                  <div className="flex items-start" data-id="element-9493">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-9494" />

                    <div data-id="element-9495">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9496">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-9497">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9498">
                  <div className="text-sm text-gray-900" data-id="element-9499">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9500">
                  <div className="text-sm text-gray-900" data-id="element-9501">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9502">
                  <div className="text-sm text-gray-900" data-id="element-9503">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9504">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9505">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-9506">
                      {t('pages.Rates.promo')}
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-9507">
                  <button aria-label="Rate actions" className="text-gray-400 hover:text-gray-500" data-id="element-9508">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-9509" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}